import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Phone, ExternalLink, Calendar, MapPin, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { whatsappChatService, ChatMessage, QuickReply } from '../services/whatsappChatService';
import { Event } from '../services/eventsService';

interface WhatsAppChatProps {
  className?: string;
}

const WhatsAppChat: React.FC<WhatsAppChatProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [userId] = useState(() => `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initialize conversation when chat is opened for the first time
      const initialMessages = whatsappChatService.initializeConversation(userId);
      setMessages(initialMessages);
    }
  }, [isOpen, userId, messages.length]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connectWhatsApp = () => {
    // This would integrate with WhatsApp Business API
    const phoneNumber = '962788000909'; // Replace with actual business number
    const welcomeMessage = 'Hello! I found your platform and would like to know more about event tickets.';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(welcomeMessage)}`, '_blank');
  };

  const sendMessage = async () => {
    if (message.trim()) {
      setIsTyping(true);
      
      try {
        const updatedMessages = await whatsappChatService.processMessage(userId, message.trim());
        setMessages([...updatedMessages]);
        setMessage('');
      } catch (error) {
        console.error('Error processing message:', error);
        // Add error message
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          text: "Sorry, I'm having trouble right now. Please try again or contact our support team.",
          sender: 'bot',
          timestamp: new Date(),
          type: 'text'
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsTyping(false);
      }
    }
  };

  const handleQuickReply = async (action: string, payload?: any) => {
    setIsTyping(true);

    try {
      if (action === 'redirect_checkout' && payload) {
        // Handle checkout redirect
        const checkoutUrl = await whatsappChatService.handleBookingRedirect(userId, payload);
        navigate(checkoutUrl);
        setIsOpen(false);
        return;
      }

      const updatedMessages = await whatsappChatService.processQuickReply(userId, action, payload);
      setMessages([...updatedMessages]);
    } catch (error) {
      console.error('Error processing quick reply:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const renderMessage = (msg: ChatMessage) => {
    if (msg.sender === 'user') {
      return (
        <div key={msg.id} className="flex justify-end mb-3">
          <div className="glass-strong text-white p-3 rounded-lg max-w-xs shadow-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <p className="text-sm">{msg.text}</p>
            <p className="text-xs opacity-75 mt-1">
              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div key={msg.id} className="flex justify-start mb-3">
        <div className="glass text-white p-3 rounded-lg max-w-xs shadow-sm">
          {msg.type === 'event_card' && msg.data?.event ? (
            <EventCard event={msg.data.event} quickReplies={msg.data.quickReplies} onQuickReply={handleQuickReply} />
          ) : (
            <div>
              <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
              <p className="text-xs text-white/70 mt-1">
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}
          
          {/* Quick Replies */}
          {msg.data?.quickReplies && msg.data.quickReplies.length > 0 && (
            <div className="mt-3 space-y-1">
              {msg.data.quickReplies.map((reply: QuickReply, index: number) => (
                <button
                  key={index}
                  onClick={() => handleQuickReply(reply.action, reply.payload)}
                  className="block w-full text-left px-3 py-2 glass-button text-white rounded-lg text-sm font-medium transition-colors glow hover:scale-105"
                >
                  {reply.text}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed bottom-20 right-4 z-50 ${className}`}>
      {/* WhatsApp Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="glass-button text-white rounded-full p-4 transition-all duration-300 hover:scale-110 animate-pulse glow float-animation group"
          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
          title={t('whatsapp.aiAssistant', 'AI Event Assistant')}
        >
          <MessageCircle size={24} />
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-500 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
            <span className="text-xs font-bold text-white">AI</span>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="glass-modal rounded-2xl w-80 h-96 flex flex-col gradient-border overflow-hidden">
          {/* Header */}
          <div className="glass-strong text-white p-4 rounded-t-2xl flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 glass rounded-full flex items-center justify-center">
                <MessageCircle size={16} />
              </div>
              <div>
                <h3 className="font-semibold">{t('whatsapp.aiAssistant', 'AI Event Assistant')}</h3>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                  <p className="text-xs text-green-100">Smart • Instant replies</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto space-y-2" style={{ background: 'linear-gradient(180deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.9) 100%)' }}>
            {messages.map(renderMessage)}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start mb-3">
                <div className="glass text-white p-3 rounded-lg shadow-sm">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/20">
            <div className="flex space-x-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isTyping && sendMessage()}
                placeholder="Type your message..."
                disabled={isTyping}
                className="flex-1 px-3 py-2 glass-input rounded-lg text-white placeholder-white/60 text-sm focus-glass disabled:opacity-50"
              />
              <button
                onClick={sendMessage}
                disabled={!message.trim() || isTyping}
                className="glass-button text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed glow transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
              >
                <Send size={16} />
              </button>
            </div>
            
            <button
              onClick={connectWhatsApp}
              className="w-full mt-2 glass-button text-white py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-all hover:scale-105 glow"
              style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
            >
              <Phone size={14} />
              <span>Continue on WhatsApp</span>
              <ExternalLink size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Event Card Component for displaying events in chat
const EventCard: React.FC<{
  event: Event;
  quickReplies?: QuickReply[];
  onQuickReply: (action: string, payload?: any) => void;
}> = ({ event, quickReplies, onQuickReply }) => {
  const { t } = useTranslation();
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const soldPercentage = Math.round((event.ticketsSold / event.capacity) * 100);
  const isPopular = soldPercentage > 70;
  const isAlmostSoldOut = soldPercentage > 90;

  return (
    <div className="glass rounded-lg overflow-hidden gradient-border bg-gradient-to-br from-blue-500/20 to-yellow-500/20">
      {/* Event Image */}
      <div className="relative h-24 bg-gradient-to-r from-yellow-400 to-blue-500 overflow-hidden">
        <img 
          src={event.image} 
          alt={event.title}
          className="w-full h-full object-cover opacity-90"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        
        {/* Status Badge */}
        {isAlmostSoldOut && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
            🔥 {t('events.almostSoldOut', 'Almost Sold Out')}
          </div>
        )}
        {isPopular && !isAlmostSoldOut && (
          <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
            ⭐ {t('events.popular', 'Popular')}
          </div>
        )}
      </div>

      {/* Event Details */}
      <div className="p-3">
        <h4 className="font-semibold text-white text-sm mb-2 line-clamp-2">{event.title}</h4>
        
        <div className="space-y-1 text-xs text-white/70">
          <div className="flex items-center space-x-1">
            <Calendar size={12} />
            <span>{formatDate(event.date)} • {event.time}</span>
          </div>
          <div className="flex items-center space-x-1">
            <MapPin size={12} />
            <span>{event.venue}</span>
          </div>
          <div className="flex items-center space-x-1">
            <DollarSign size={12} />
            <span className="font-semibold text-green-300">${event.price}</span>
            <span className="text-white/50">• {event.capacity - event.ticketsSold} left</span>
          </div>
        </div>

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {event.tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="px-2 py-1 glass text-white rounded-full text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Quick Action Buttons */}
        {quickReplies && quickReplies.length > 0 && (
          <div className="mt-3 space-y-1">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => onQuickReply(reply.action, reply.payload)}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                  reply.text.includes('Book') 
                    ? 'glass-button text-white glow' 
                    : 'glass text-white hover:bg-white/10'
                }`}
                style={reply.text.includes('Book') ? { background: 'linear-gradient(135deg, #25D366, #128C7E)' } : {}}
              >
                {reply.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppChat; 